import React, { useState, useCallback } from 'react';
import { createDebouncedValidator, ValidationResult } from '../../utils/validation';

interface ValidatedFormFieldProps {
  id: string;
  label: string;
  type?: 'text' | 'email' | 'tel' | 'number';
  value: string | number;
  error?: string | null;
  placeholder?: string;
  required?: boolean;
  className?: string;
  // Numeric input constraints, forwarded to the underlying input when given
  step?: number | string;
  min?: number | string;
  max?: number | string;
  onChange: (name: string, value: string | number) => void;
  onClearError?: (name: string) => void;
  validationFn?: (value: string | number) => ValidationResult;
  enableRealTimeValidation?: boolean;
  children?: React.ReactNode; // For additional content like help text
}

export default function ValidatedFormField({
  id,
  label,
  type = 'text',
  value,
  error,
  placeholder,
  required = false,
  className = '',
  step,
  min,
  max,
  onChange,
  onClearError,
  validationFn,
  enableRealTimeValidation = false,
  children
}: ValidatedFormFieldProps) {
  const [localError, setLocalError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [hasBeenTouched, setHasBeenTouched] = useState(false);

  // Create debounced validator if validation function is provided
  const debouncedValidator = useCallback(() => {
    if (!validationFn) return null;

    return createDebouncedValidator((value: string | number) => {
      return validationFn(value);
    }, 300);
  }, [validationFn]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value;

    // Clear server error when user starts typing
    if (error && onClearError) {
      onClearError(id);
    }

    // Clear local error when user starts typing
    if (localError) {
      setLocalError(null);
    }

    onChange(id, newValue);

    // Perform real-time validation if enabled and function is provided
    if (enableRealTimeValidation && validationFn && hasBeenTouched) {
      const validator = debouncedValidator();
      if (validator) {
        setIsValidating(true);
        validator(newValue, (result: ValidationResult) => {
          setLocalError(result.error);
          setIsValidating(false);
        });
      }
    }
  };

  const handleBlur = () => {
    setHasBeenTouched(true);

    // Validate on blur if validation function is provided
    if (validationFn) {
      const result = validationFn(value);
      setLocalError(result.error);
    }
  };

  const handleFocus = () => {
    // Clear local validation error when user focuses on field
    if (localError) {
      setLocalError(null);
    }
  };

  // Determine which error to show (server error takes precedence)
  const displayError = error || localError;
  const hasError = Boolean(displayError);
  const showValidation = hasBeenTouched || Boolean(error);

  // Determine field status for styling
  const isValid = showValidation && !hasError && value !== '' && value !== 0;
  const isInvalid = showValidation && hasError;

  const inputClasses = [
    'form-control',
    className,
    isValid ? 'is-valid' : '',
    isInvalid ? 'is-invalid' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className="modern-form-group">
      <label htmlFor={id} className={`${required ? 'required' : ''}`}>
        {label}
        {isValidating && (
          <span className="ms-2 loading-spinner">
            <div className="spinner spinner-small"></div>
            <small>Validating...</small>
          </span>
        )}
      </label>

      <div className="input-with-validation">
        <input
          type={type}
          className={inputClasses}
          id={id}
          name={id}
          value={value}
          placeholder={placeholder}
          step={step}
          min={min}
          max={max}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          required={required}
          aria-describedby={displayError ? `${id}-error` : undefined}
        />

        {/* Success feedback - positioned to the right */}
        {isValid && (
          <div className="valid-feedback-icon">
            <i className="fas fa-check-circle"></i>
          </div>
        )}
      </div>

      {/* Error feedback */}
      {displayError && (
        <div className="invalid-feedback show" id={`${id}-error`}>
          <i className="fas fa-exclamation-circle"></i> {displayError}
        </div>
      )}

      {/* Additional content like help text */}
      {children && (
        <div className="form-text">
          {children}
        </div>
      )}
    </div>
  );
}
